import {
  add,
  areIntervalsOverlapping,
  compareAsc,
  differenceInMilliseconds,
  getMilliseconds,
  Interval,
  max,
  min,
  startOfDay,
} from 'date-fns';

export function durationInMills(duration: Duration): number {
  return getMilliseconds(add(0, duration));
}

export function mergeTakenTimeSlots(timeSlots: Interval[]) {
  const sortedByStartTime = timeSlots.sort((i1, i2) =>
    compareAsc(i1.start, i2.start),
  );
  const mergedTimeSlots: Interval[] = [];
  for (let i = 0; i < sortedByStartTime.length; i++) {
    let currentInterval = sortedByStartTime[i];
    for (let j = i + 1; j < sortedByStartTime.length; j++, i++) {
      const nextInterval = sortedByStartTime[j];
      if (compareAsc(nextInterval.start, currentInterval.end) > 0) {
        break;
      } else {
        currentInterval = {
          start: currentInterval.start,
          end:
            compareAsc(currentInterval.end, nextInterval.end) >= 0
              ? currentInterval.end
              : nextInterval.end,
        };
      }
    }
    mergedTimeSlots.push(currentInterval);
  }
  return mergedTimeSlots;
}

export function isSelectedIntervalAlreadyTaken(
  selectedInterval: Interval,
  takenTimeSlots: Interval[],
): boolean {
  return !!takenTimeSlots.find((takenSlot) =>
    areIntervalsOverlapping(takenSlot, selectedInterval),
  );
}

export function convertIntervalToFormField(interval: Interval) {
  return {
    startDate: startOfDay(interval.start),
    startTimeInMills: differenceInMilliseconds(
      interval.start,
      startOfDay(interval.start),
    ),
    endDate: startOfDay(interval.end),
    endTimeInMills: differenceInMilliseconds(
      interval.end,
      startOfDay(interval.end),
    ),
  };
}

/**
 * Find intersections (as intervals) of multiple interval groups (e.g. time intervals where ALL the rooms are occupied)
 */
export function findIntersectionsBetweenManyIntervalGroups(
  intervalGroups: Interval[][],
): Interval[] {
  if (intervalGroups.length === 0) {
    return [];
  }
  if (intervalGroups.length === 1) {
    return intervalGroups[0];
  }

  let result: Interval[] = intervalGroups[0];
  for (let i = 1; i < intervalGroups.length; i++) {
    result = findIntersectionsBetweenTwoIntervalGroups(
      result,
      intervalGroups[i],
    );
    if (result.length === 0) break;
  }
  return result;
}

/**
 * Find intersections of two interval groups using two pointers technique
 * Each group should be pairwise disjoint and in sorted order (!)
 * @param firstGroup
 * @param secondGroup
 */
export function findIntersectionsBetweenTwoIntervalGroups(
  firstGroup: Interval[],
  secondGroup: Interval[],
): Interval[] {
  const result: Interval[] = [];
  let firstIndex = 0; // pointer for the first group
  let secondIndex = 0; // pointer for the second group
  while (firstIndex < firstGroup.length && secondIndex < secondGroup.length) {
    const intervalA = firstGroup[firstIndex];
    const intervalB = secondGroup[secondIndex];
    if (areIntervalsOverlapping(intervalA, intervalB)) {
      result.push({
        start: max([intervalA.start, intervalB.start]),
        end: min([intervalA.end, intervalB.end]),
      });
    }
    if (intervalA.end > intervalB.end) {
      secondIndex++;
    } else {
      firstIndex++;
    }
  }
  return result;
}
